import React, { useState, useMemo } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NHLogo from '../components/NHLogo';

const ROLE_LABELS = {
  practice_admin: 'Practice Admin',
  clinical_admin: 'Clinical Admin',
  care_provider: 'Care Provider',
};

function SelectContext() {
  const { pendingContextSelection, selectContext, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  const [selectedPractice, setSelectedPractice] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Flat list of practice options (API now returns flat list directly)
  const practiceOptions = useMemo(() => {
    if (!pendingContextSelection?.contexts) return [];
    const contexts = pendingContextSelection.contexts;
    // Handle both new flat format and legacy nested format
    if (contexts.length > 0 && contexts[0].practiceId) {
      // New flat format: [{ practiceId, practiceName, practiceGroupId, practiceGroupName, role }]
      return contexts;
    }
    // Legacy nested format: [{ practiceGroupId, role, practices: [...] }]
    const options = [];
    for (const ctx of contexts) {
      for (const practice of (ctx.practices || [])) {
        options.push({
          practiceId: practice.id,
          practiceName: practice.name,
          practiceGroupId: ctx.practiceGroupId,
          practiceGroupName: ctx.practiceGroupName,
          role: ctx.role,
        });
      }
    }
    return options;
  }, [pendingContextSelection]);

  // Unique practices (a practice may appear under multiple roles)
  const uniquePractices = useMemo(() => {
    const seen = new Map();
    for (const opt of practiceOptions) {
      if (!seen.has(opt.practiceId)) {
        seen.set(opt.practiceId, {
          id: opt.practiceId,
          name: opt.practiceName,
          practiceGroupId: opt.practiceGroupId,
          practiceGroupName: opt.practiceGroupName,
        });
      }
    }
    return Array.from(seen.values());
  }, [practiceOptions]);

  // Available roles for the selected practice
  const availableRoles = useMemo(() => {
    if (!selectedPractice) return [];
    return practiceOptions
      .filter(opt => opt.practiceId === selectedPractice.id)
      .map(opt => opt.role);
  }, [selectedPractice, practiceOptions]);

  // Group practices by practice group for display
  const practicesByGroup = useMemo(() => {
    const groups = {};
    for (const p of uniquePractices) {
      const groupName = p.practiceGroupName || 'Ungrouped';
      if (!groups[groupName]) {
        groups[groupName] = { practiceGroupId: p.practiceGroupId, practices: [] };
      }
      groups[groupName].practices.push(p);
    }
    return groups;
  }, [uniquePractices]);

  // If already authenticated, skip this page
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // If no pending context selection, redirect to login
  if (!pendingContextSelection) {
    return <Navigate to="/login" replace />;
  }

  const handlePracticeSelect = (practice) => {
    setSelectedPractice(practice);
    setSelectedRole(null);
    setError('');
  };

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    setError('');
  };

  const handleContinue = async () => {
    if (!selectedPractice || !selectedRole) return;

    setError('');
    setIsLoading(true);

    // Find the matching context to get practiceGroupId
    const match = practiceOptions.find(
      opt => opt.practiceId === selectedPractice.id && opt.role === selectedRole
    );

    if (!match) {
      setError('Invalid selection');
      setIsLoading(false);
      return;
    }

    const result = await selectContext(match.practiceGroupId, match.role, match.practiceId);

    if (result.success) {
      navigate('/', { replace: true });
    } else {
      setError(result.error || 'Failed to select context');
    }

    setIsLoading(false);
  };

  const handleBack = () => {
    if (selectedRole) {
      setSelectedRole(null);
    } else if (selectedPractice) {
      setSelectedPractice(null);
    } else {
      logout();
      navigate('/login', { replace: true });
    }
  };

  const userName = pendingContextSelection.user
    ? `${pendingContextSelection.user.first_name || ''} ${pendingContextSelection.user.last_name || ''}`.trim()
    : '';

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <NHLogo size={36} style={{ justifyContent: 'center' }} />
          <p style={styles.subtitle}>
            {userName ? `Welcome, ${userName}` : 'Select your workspace'}
          </p>
        </div>

        <div style={styles.body}>
          {error && <div style={styles.error}>{error}</div>}

          {/* Step 1: Select Practice */}
          {!selectedPractice && (
            <>
              <h2 style={styles.stepTitle}>Select Practice</h2>
              <p style={styles.stepDescription}>Choose the practice you want to work in</p>
              <div style={styles.optionsList}>
                {Object.entries(practicesByGroup).map(([groupName, group]) => (
                  <div key={groupName}>
                    {Object.keys(practicesByGroup).length > 1 && (
                      <div style={styles.groupLabel}>{groupName}</div>
                    )}
                    {group.practices.map(practice => (
                      <button
                        key={practice.id}
                        style={styles.optionButton}
                        onClick={() => handlePracticeSelect(practice)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#667eea';
                          e.currentTarget.style.background = '#f8f9ff';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#e5e7eb';
                          e.currentTarget.style.background = '#fff';
                        }}
                      >
                        <span style={styles.optionName}>{practice.name}</span>
                        <span style={styles.optionArrow}>&rarr;</span>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Step 2: Select Role */}
          {selectedPractice && !selectedRole && (
            <>
              <h2 style={styles.stepTitle}>Select Role</h2>
              <p style={styles.stepDescription}>
                Choose your role for <strong>{selectedPractice.name}</strong>
              </p>
              <div style={styles.optionsList}>
                {availableRoles.map(role => (
                  <button
                    key={role}
                    style={styles.optionButton}
                    onClick={() => handleRoleSelect(role)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#667eea';
                      e.currentTarget.style.background = '#f8f9ff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.background = '#fff';
                    }}
                  >
                    <span style={styles.optionName}>{ROLE_LABELS[role] || role}</span>
                    <span style={styles.optionArrow}>&rarr;</span>
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Step 3: Confirm */}
          {selectedPractice && selectedRole && (
            <>
              <h2 style={styles.stepTitle}>Confirm Selection</h2>
              <div style={styles.confirmBox}>
                <div style={styles.confirmRow}>
                  <span style={styles.confirmLabel}>Practice</span>
                  <span style={styles.confirmValue}>{selectedPractice.name}</span>
                </div>
                <div style={styles.confirmRow}>
                  <span style={styles.confirmLabel}>Role</span>
                  <span style={styles.confirmValue}>{ROLE_LABELS[selectedRole] || selectedRole}</span>
                </div>
              </div>
              <button
                style={{
                  ...styles.continueButton,
                  ...(isLoading ? styles.buttonDisabled : {})
                }}
                onClick={handleContinue}
                disabled={isLoading}
              >
                {isLoading ? 'Starting session...' : 'Continue'}
              </button>
            </>
          )}

          <button style={styles.backButton} onClick={handleBack} disabled={isLoading}>
            {!selectedPractice ? 'Back to Login' : 'Back'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '20px',
  },
  card: {
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
    width: '100%',
    maxWidth: '460px',
    overflow: 'hidden',
  },
  header: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '30px',
    textAlign: 'center',
    color: '#fff',
  },
  title: {
    margin: 0,
    fontSize: '28px',
    fontWeight: '700',
  },
  subtitle: {
    margin: '8px 0 0',
    fontSize: '14px',
    opacity: 0.9,
  },
  body: {
    padding: '30px',
  },
  error: {
    background: '#fee',
    color: '#c00',
    padding: '12px',
    borderRadius: '6px',
    marginBottom: '20px',
    fontSize: '14px',
    textAlign: 'center',
  },
  stepTitle: {
    margin: '0 0 4px',
    fontSize: '20px',
    color: '#333',
    textAlign: 'center',
  },
  stepDescription: {
    margin: '0 0 20px',
    fontSize: '14px',
    color: '#666',
    textAlign: 'center',
  },
  optionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '20px',
  },
  groupLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#999',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    padding: '8px 0 4px',
  },
  optionButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '14px 16px',
    fontSize: '15px',
    fontWeight: '500',
    color: '#333',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'border-color 0.2s, background 0.2s',
    textAlign: 'left',
  },
  optionName: {
    flex: 1,
  },
  optionArrow: {
    color: '#999',
    fontSize: '16px',
    marginLeft: '8px',
  },
  confirmBox: {
    background: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '20px',
  },
  confirmRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
  },
  confirmLabel: {
    fontSize: '14px',
    color: '#666',
  },
  confirmValue: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#333',
  },
  continueButton: {
    width: '100%',
    padding: '14px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#fff',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'opacity 0.2s',
    marginBottom: '12px',
  },
  buttonDisabled: {
    opacity: 0.7,
    cursor: 'not-allowed',
  },
  backButton: {
    width: '100%',
    padding: '10px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#666',
    background: 'transparent',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
};

export default SelectContext;
